import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock3, ListChecks, Loader2, Percent, PhoneCall, PhoneOff, PhoneOutgoing, Timer, TriangleAlert, Users } from "lucide-react";
import { BarList, ColumnChart, DataTable, ReportSkeleton, SectionCard, StatTile, fmtPct, type Tone } from "@/components/reports/ReportWidgets";
import { buildQuery, useReport, type DateRange } from "@/components/reports/DateRangeFilter";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useAiFeature } from "@/lib/ai";
import { AiIcon } from "@/components/ai/AiIcon";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RecordingPlayer } from "@/components/calling/RecordingPlayer";
import { IncomingCallsReport } from "./IncomingCallsReport";
import { dateLocale, withClock } from "@/lib/dateTime";

const OUTCOME: Record<string, string> = { INTERESTED: "Interested", CALL_BACK: "Call back later", NOT_INTERESTED: "Not interested", NO_ANSWER: "No answer", BUSY: "Busy", VOICEMAIL: "Voicemail", WRONG_NUMBER: "Wrong number", NONE: "No outcome" };
const RESULT: Record<string, string> = { FINE: "Looks fine", COACHING: "Needs coaching", WRONG_OUTCOME: "Wrong outcome" };
const LINK: Record<string, string> = { LEAD: "/leads", CONTACT: "/contacts", DEAL: "/deals", ACCOUNT: "/accounts" };

const mins = (s: number) => (s >= 3600 ? `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min` : `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")} s`);
const clock = (s: number | null) => (s == null ? "–" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
const when = (d: string) => new Date(d).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }));

interface CallsData {
  connected_min_sec: number;
  totals: Totals;
  previous: Totals;
  daily: Array<{ day: string; attempts: number; connected: number }>;
  outcomes: Array<{ outcome: string; calls: number }>;
  timezone: string;
  heat: Array<{ dow: number; hour: number; attempts: number; connected: number }>;
  sources: Array<{ source: string; attempts: number; connected: number; contact_rate_pct: number; interested: number }>;
  rows: Array<{ won: number; calls_per_won: number | null; talk_min_per_won: number | null; user_id: string; name: string; attempts: number; answered: number; not_answered: number; connected: number; contact_rate_pct: number; talk_sec: number; avg_talk_sec: number; interested: number; no_outcome: number }>;
}
interface Totals { attempts: number; answered: number; connected: number; contact_rate_pct: number; talk_sec: number; avg_talk_sec: number; no_outcome: number }
interface ReviewRow { id: string; rep: string; related_type: string; related_id: string; started_at: string; duration_sec: number | null; outcome: string | null; note: string | null; has_recording: boolean; reason: string | null; reviewed: boolean; review_result: string | null; review_note: string | null; reviewed_by: string | null }

function Tile({ label, value, sub, tone = "blue", icon }: { label: string; value: string; sub: string; tone?: Tone; icon?: React.ReactNode }) {
  return <StatTile label={label} value={value} tone={tone} icon={icon} hint={<span>{sub}</span>} />;
}

const vs = (now: number, before: number) => (before === 0 ? (now === 0 ? "nothing in either period" : "nothing the period before") : `${now >= before ? "▲" : "▼"} ${Math.abs(Math.round(((now - before) / before) * 100))}% vs the period before`);

export function CallsTab({ range, headId, userId = "", outcome = "", direction, onDirection }: { range: DateRange; headId: string; userId?: string; outcome?: string; direction: "out" | "in"; onDirection: (d: "out" | "in") => void }) {
  return (
    <div className="space-y-4">
      <div role="tablist" aria-label="Call direction" className="flex gap-0.5 border-b border-border">
        {([["out", "Outgoing"], ["in", "Incoming"]] as const).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={direction === k} onClick={() => onDirection(k)} className={direction === k ? "h-[34px] border-b-2 border-primary px-3 text-[13px] font-semibold" : "h-[34px] border-b-2 border-transparent px-3 text-[13px] text-muted-foreground hover:text-foreground"}>
            {label}
          </button>
        ))}
      </div>
      {direction === "out" ? (
        <OutgoingCalls range={range} headId={headId} userId={userId} outcome={outcome} />
      ) : (
        <IncomingCallsReport range={range} headId={headId} userId={userId} />
      )}
    </div>
  );
}

function OutgoingCalls({ range, headId, userId, outcome }: { range: DateRange; headId: string; userId: string; outcome: string }) {
  const { hasPermission } = useAuth();
  const team = hasPermission("report.view_team");
  const query = buildQuery({ from: range.from, to: range.to, head_id: headId, user_id: userId });
  const { data, loading, error } = useReport<CallsData>(`/api/Report/Calls${query}${outcome ? `&outcome=${encodeURIComponent(outcome)}` : ""}`);
  const t = data?.totals;
  const p = data?.previous;

  return (
    <div className="relative space-y-4">
      {loading && data && <Loader2 className="absolute right-2 top-2 size-4 animate-spin text-primary" />}
      {error && <p className="py-6 text-center text-sm text-destructive">{error}</p>}
      {!data && !error && <ReportSkeleton tiles={4} cards={2} />}
      {t && p && (
        <>
          <div className="grid grid-cols-2 gap-3 @2xl/main:grid-cols-3 @6xl/main:grid-cols-6">
            <Tile label="Calls made" value={String(t.attempts)} sub={vs(t.attempts, p.attempts)} icon={<PhoneOutgoing />} />
            <Tile label="Connected" value={String(t.connected)} sub={`answered and talked ${data?.connected_min_sec}+ s`} tone="green" icon={<PhoneCall />} />
            <Tile label="Not answered" value={String(t.attempts - t.answered)} sub="nobody picked up" tone={t.attempts - t.answered ? "red" : "gray"} icon={<PhoneOff />} />
            <Tile label="Connection rate" value={fmtPct(t.contact_rate_pct)} sub={`connected ÷ calls made · before ${fmtPct(p.contact_rate_pct)}`} tone="purple" icon={<Percent />} />
            <Tile label="Talk time" value={mins(t.talk_sec)} sub={`average call ${mins(t.avg_talk_sec)}`} icon={<Timer />} />
            <Tile label="No outcome" value={String(t.no_outcome)} sub={t.no_outcome ? "calls with no result chosen" : "all calls have a result"} tone={t.no_outcome ? "orange" : "green"} icon={<TriangleAlert />} />
          </div>
          <div className="grid gap-4 @4xl/main:grid-cols-2">
            <SectionCard title="Calls per day" description="Calls made, and how many connected" icon={<Clock3 />}>
              <ColumnChart
                data={data.daily.map((d) => ({ label: new Date(`${d.day}T00:00:00`).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" }), value: d.attempts, detail: [`${d.connected} connected`] }))}
                emptyMessage="No calls in this period."
              />
            </SectionCard>
            <SectionCard title="How calls ended" description="The result the rep chose after each call" icon={<ListChecks />} tone="purple">
              <BarList tone="purple" data={data.outcomes.map((o) => ({ label: OUTCOME[o.outcome] || o.outcome, value: o.calls }))} emptyMessage="No calls in this period." />
            </SectionCard>
          </div>
          <div className="grid gap-4 @4xl/main:grid-cols-[1.4fr_1fr]">
            <SectionCard title="Best time to call" description={`Contact rate by weekday and hour (${data.timezone.replace("_", " ")}). Darker = more calls connect.`}>
              <HeatMap cells={data.heat} />
            </SectionCard>
            <SectionCard title="Lead source quality" description="Which sources pick up the phone">
              <DataTable
                columns={[
                  { key: "source", header: "Source", render: (r) => <span className="font-medium text-foreground">{r.source}</span> },
                  { key: "attempts", header: "Calls", align: "right" },
                  { key: "contact_rate_pct", header: "Contact rate", align: "right", render: (r) => fmtPct(r.contact_rate_pct) },
                  { key: "interested", header: "Interested", align: "right" },
                ]}
                rows={data.sources}
                rowKey={(r) => r.source}
                defaultSort={{ key: "attempts", dir: "desc" }}
                emptyMessage="No lead calls in this period."
              />
            </SectionCard>
          </div>
          <SectionCard title="By salesperson" description="Sorted by connected calls. Calls per won deal ties effort to results." icon={<Users />}>
            <DataTable
              columns={[
                { key: "name", header: "Rep", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
                { key: "attempts", header: "Calls", align: "right" },
                { key: "not_answered", header: "Not answered", align: "right", render: (r) => <span className={r.not_answered ? "text-danger" : ""}>{r.not_answered}</span> },
                { key: "connected", header: "Connected", align: "right", render: (r) => <span className="font-semibold text-success">{r.connected}</span> },
                { key: "contact_rate_pct", header: "Contact rate", align: "right", render: (r) => fmtPct(r.contact_rate_pct) },
                { key: "talk_sec", header: "Talk time", align: "right", render: (r) => mins(r.talk_sec) },
                { key: "avg_talk_sec", header: "Average", align: "right", render: (r) => mins(r.avg_talk_sec) },
                { key: "interested", header: "Interested", align: "right" },
                { key: "no_outcome", header: "No outcome", align: "right" },
                { key: "won", header: "Deals won", align: "right" },
                { key: "calls_per_won", header: "Calls per won deal", align: "right", render: (r) => (r.calls_per_won ?? "–") },
                { key: "talk_min_per_won", header: "Talk min per won deal", align: "right", render: (r) => (r.talk_min_per_won ?? "–") },
              ]}
              rows={data.rows}
              rowKey={(r) => r.user_id}
              defaultSort={{ key: "connected", dir: "desc" }}
              emptyMessage="No calls in this period."
            />
          </SectionCard>
        </>
      )}
      {team && <LiveBoard headId={headId} />}
      {team && <ReviewQueue query={query} />}
      {team && <CoachingSection range={range} />}
    </div>
  );
}

interface RepScore {
  user_id: string;
  name: string;
  calls: number;
  average_score: number;
}
interface RepCoaching {
  calls: number;
  average_score: number | null;
  summary: string | null;
  strengths: string[];
  focus: { what: string; why: string }[];
}

/** AI coaching scores from summarised calls, per rep, and a coaching summary for one rep (Sales Heads, Super Admins) */
function CoachingSection({ range }: { range: DateRange }) {
  const on = useAiFeature("CALL_COACHING");
  const { error } = useToast();
  const [rows, setRows] = useState<RepScore[] | null>(null);
  const [rep, setRep] = useState<RepScore | null>(null);
  const [coach, setCoach] = useState<RepCoaching | null>(null);
  const [busy, setBusy] = useState(false);
  const q = buildQuery({ from: range.from, to: range.to });
  useEffect(() => {
    if (!on) return;
    api
      .get(`/api/Ai/CallCoaching/Scores${q}`)
      .then((r) => setRows(unwrapArray<RepScore>(r)))
      .catch(() => setRows([]));
  }, [on, q]);
  if (!on || !rows) return null;
  const open = async (r: RepScore) => {
    setRep(r);
    setCoach(null);
    setBusy(true);
    try {
      setCoach(unwrapObject<RepCoaching>(await api.get(`/api/Ai/CallCoaching/Rep/${r.user_id}${q}`)));
    } catch (e: any) {
      error("Couldn't prepare the coaching summary", e.message || "Please try again");
      setRep(null);
    } finally {
      setBusy(false);
    }
  };
  return (
    <SectionCard title="AI call coaching" description="Average coaching score (1–10) from the calls someone summarised with AI in this period. Open a rep for what to work on.">
      {rows.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">No summarised calls in this period yet. Press Summarise on a recorded call (Calls page) to start.</p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <li key={r.user_id} className="flex items-center justify-between gap-3 py-2 text-[13px]">
              <span className="font-medium">{r.name}</span>
              <span className="flex items-center gap-3">
                <span className="text-muted-foreground">{r.calls} call{r.calls === 1 ? "" : "s"}</span>
                <span className={`font-mono font-semibold ${r.average_score >= 8 ? "text-success" : r.average_score >= 5 ? "text-warning" : "text-destructive"}`}>{r.average_score}/10</span>
                <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => void open(r)}>
                  <AiIcon className="size-3.5" /> Coaching summary
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {rep && (
        <Dialog open onOpenChange={(o) => !o && setRep(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Coaching for {rep.name}</DialogTitle>
              <DialogDescription>From {rep.calls} summarised call{rep.calls === 1 ? "" : "s"} in this period · average {rep.average_score}/10</DialogDescription>
            </DialogHeader>
            {busy || !coach ? (
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground"><AiIcon className="size-4" busy /> Reading the call notes…</p>
            ) : (
              <div className="space-y-3 text-[13px]">
                {coach.summary && <p>{coach.summary}</p>}
                {coach.strengths.length > 0 && (
                  <div>
                    <span className="text-xs font-semibold text-success">Strengths</span>
                    <ul className="list-disc pl-4">{coach.strengths.map((s) => <li key={s}>{s}</li>)}</ul>
                  </div>
                )}
                {coach.focus.length > 0 && (
                  <div>
                    <span className="text-xs font-semibold text-warning">Work on</span>
                    <ol className="list-decimal space-y-1 pl-4">
                      {coach.focus.map((f) => (
                        <li key={f.what}>
                          <span className="font-medium">{f.what}</span>
                          <span className="block text-xs text-muted-foreground">{f.why}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </SectionCard>
  );
}

function LiveBoard({ headId }: { headId: string }) {
  const { data, reload } = useReport<{ reps: Array<{ user_id: string; name: string; state: "TALKING" | "RINGING" | "IDLE"; since: string | null; calls_today: number }> }>(`/api/Report/CallsLive${buildQuery({ head_id: headId })}`);
  useEffect(() => {
    const t = setInterval(reload, 15000);
    return () => clearInterval(t);
  }, [reload]);
  const reps = data?.reps || [];
  const live = reps.filter((r) => r.state !== "IDLE").length;
  return (
    <SectionCard title="Team now" description={reps.length ? `${live} on a call · updates by itself` : "Reps who can call appear here"}>
      {reps.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">No reps have calling turned on yet.</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 @4xl/main:grid-cols-4">
          {reps.map((r) => (
            <div key={r.user_id} className="flex items-center gap-2.5 rounded-lg border border-border px-3 py-2">
              <span className={`size-2.5 shrink-0 rounded-full ${r.state === "TALKING" ? "bg-success" : r.state === "RINGING" ? "bg-warning" : "bg-muted-foreground/40"}`} />
              <div className="min-w-0 text-sm">
                <div className="truncate font-medium">{r.name}</div>
                <div className="text-xs text-muted-foreground">{r.state === "TALKING" ? "On a call" : r.state === "RINGING" ? "Ringing" : "Free"} · {r.calls_today} today</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function ReviewQueue({ query }: { query: string }) {
  const { data, reload } = useReport<{ rows: ReviewRow[]; open: number }>(`/api/Report/CallsReview${query}`);
  const { success, error } = useToast();
  const [target, setTarget] = useState<ReviewRow | null>(null);
  const [result, setResult] = useState("FINE");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const rows = data?.rows || [];

  const open = (r: ReviewRow) => { setTarget(r); setResult("FINE"); setNote(""); };
  const save = async () => {
    if (!target) return;
    setSaving(true);
    try {
      await api.post(`/api/Report/CallsReview/${target.id}`, { result, note: note.trim() || undefined });
      success("Review saved", result === "FINE" && !note.trim() ? "" : "The rep was notified.");
      setTarget(null);
      await reload();
    } catch (e: any) {
      error("Couldn't save the review", e?.message || "Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Calls to review" description={`${data?.open ?? 0} to look at · short "Interested" calls, very long calls and calls with no result`}>
      <DataTable
        columns={[
          { key: "rep", header: "Rep", render: (r: ReviewRow) => <span className="font-medium text-foreground">{r.rep}</span> },
          { key: "started_at", header: "When", render: (r: ReviewRow) => when(r.started_at) },
          { key: "duration_sec", header: "Length", align: "right", render: (r: ReviewRow) => clock(r.duration_sec) },
          { key: "outcome", header: "Result", render: (r: ReviewRow) => OUTCOME[r.outcome || "NONE"] },
          { key: "reason", header: "Why", render: (r: ReviewRow) => r.reviewed ? <span className="text-muted-foreground">{RESULT[r.review_result || ""]} · {r.reviewed_by}</span> : r.reason },
          { key: "id", header: "", align: "right", render: (r: ReviewRow) => (
            <span className="flex items-center justify-end gap-3">
              {LINK[r.related_type] && <Link to={`${LINK[r.related_type]}/${r.related_id}`} className="text-primary hover:underline">Open</Link>}
              {!r.reviewed && <Button size="sm" variant="outline" onClick={() => open(r)}>Review</Button>}
            </span>
          ) },
        ]}
        rows={rows}
        rowKey={(r: ReviewRow) => r.id}
        emptyMessage="Nothing to review in this period."
      />
      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review {target?.rep}'s call</DialogTitle>
            <DialogDescription>{target && `${when(target.started_at)} · ${clock(target.duration_sec)} · ${OUTCOME[target.outcome || "NONE"]}${target.reason ? ` · ${target.reason}` : ""}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {target?.note && <p className="rounded-md bg-panel px-2.5 py-2 text-sm"><span className="text-xs text-muted-foreground">Rep's note: </span>{target.note}</p>}
            {target?.has_recording && <RecordingPlayer callId={target.id} />}
            <div className="flex flex-wrap gap-2">
              {Object.entries(RESULT).map(([k, label]) => (
                <button key={k} type="button" onClick={() => setResult(k)} className={`rounded-md border px-3 py-1.5 text-sm ${result === k ? "border-primary bg-accent text-accent-foreground" : "border-border"}`}>{label}</button>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="review-note">Note to the rep (optional)</Label>
              <Textarea id="review-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder="What went well, or what to change" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving && <Loader2 className="size-4 animate-spin" />} Mark reviewed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** Weekday × hour grid: how often calls connect. Cells with fewer than 3 calls stay blank so luck doesn't read as a pattern. */
function HeatMap({ cells }: { cells: Array<{ dow: number; hour: number; attempts: number; connected: number }> }) {
  if (cells.length === 0) return <p className="py-6 text-center text-sm text-muted-foreground">No calls in this period.</p>;
  const hours = cells.map((c) => c.hour);
  const from = Math.min(...hours), to = Math.max(...hours);
  const range = Array.from({ length: to - from + 1 }, (_, i) => from + i);
  const at = (dow: number, hour: number) => cells.find((c) => c.dow === dow && c.hour === hour);
  const label = (h: number) => `${h % 12 || 12}${h < 12 ? "a" : "p"}`;
  return (
    <div className="overflow-x-auto">
      <table className="border-separate border-spacing-0.5 text-[11px]">
        <thead>
          <tr>
            <th />
            {range.map((h) => <th key={h} className="px-0.5 text-center font-normal text-muted-foreground">{label(h)}</th>)}
          </tr>
        </thead>
        <tbody>
          {DOW_ORDER.map((d, i) => (
            <tr key={d}>
              <th className="pr-1.5 text-right font-normal text-muted-foreground">{DAYS[i]}</th>
              {range.map((h) => {
                const c = at(d, h);
                const rate = c && c.attempts >= 3 ? c.connected / c.attempts : null;
                return (
                  <td key={h} title={c ? `${DAYS[i]} ${label(h)}: ${c.connected} of ${c.attempts} connected${c.attempts < 3 ? " (too few calls to judge)" : ""}` : `${DAYS[i]} ${label(h)}: no calls`}
                    className="size-6 min-w-6 rounded-sm text-center" style={{ background: rate === null ? "transparent" : `color-mix(in srgb, var(--primary) ${Math.round(12 + rate * 80)}%, transparent)`, border: rate === null ? "1px dashed var(--border)" : undefined }}>
                    {rate !== null && <span className={rate > 0.55 ? "text-white" : "text-foreground"}>{Math.round(rate * 100)}</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
